import useFormWithSchema from "../../hooks/useFormWithSchema";
import { useTranslation } from "react-i18next";
import { Controller } from "react-hook-form";
import TextInput from "../../components/inputs/TextInput";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "../../components/button";
import CheckboxInput from "../../components/inputs/CheckboxInput";
import RolesTable from "../../components/table/RolesTable";
import { useGetAllPrivileges } from "../../hooks/common/query";
import toast, { Toaster } from "react-hot-toast";
import {
  useAddRolePrivileges,
  useGetRolePrivileges,
} from "../../hooks/user/mutation";
import { RolePrivilege } from "../../store/Auth/interface";
import useAuthStore from "../../store/Auth";
import { USER_ROLES_IDS } from "../../constants/common";
import { ManageRolesSchema } from "../../schemas/manageRolesSchema";
import BackButtonWrapper from "../../components/button/BackButton/wrapper";
import { ROUTE_NAMES } from "../../routes";

interface ManageRolesData {
  roleNameEn: string;
  roleNameAr: string;
}

type Privilege = {
  n_PrviligeID: number;
  s_PrivName: string;
  s_PrivNameAr: string;
  s_Priv_Desc: string;
  s_ParentMenu: string;
  parentDesc: string;
  s_formName: string | null;
  s_formName_Web: string;
  n_UserID: number;
  n_IsDeleted: number;
  n_IsShown: boolean;
  n_Order: number;
};

type NestedGroupedPrivileges = {
  [parentMenu: string]: {
    [parentDesc: string]: Privilege[];
  };
};

const ManageRoles = () => {
  const {
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    reset,
  } = useFormWithSchema(ManageRolesSchema);

  const setUserRoles = useAuthStore((state) => state.setUserRoles);

  const [selectedRoleId, setSelectedRoleId] = useState<number>(0);

  const [selectedPrivileges, setSelectedPrivileges] = useState<number[]>([]);

  const { t } = useTranslation();

  const { data: priveleges } = useGetAllPrivileges();

  const groupedPrivileges = useMemo(
    () =>
      priveleges?.reduce((acc: any, curr: any) => {
        const parent = curr.s_ParentMenu;

        if (!acc[parent]) {
          acc[parent] = [];
        }

        acc[parent].push(curr);

        return acc;
      }, {}),
    [priveleges]
  );

  const transformGroupedPrivileges = (
    groupedPrivileges: Record<string, Privilege[]>
  ): NestedGroupedPrivileges => {
    if (!groupedPrivileges) return {};

    const result: NestedGroupedPrivileges = {};

    Object.entries(groupedPrivileges).forEach(([menu, privileges]) => {
      privileges.forEach((priv) => {
        if (!result[menu]) result[menu] = {};
        if (!result[menu][priv.parentDesc]) result[menu][priv.parentDesc] = [];
        result[menu][priv.parentDesc].push(priv);
      });
    });

    return result;
  };

  const nestedGrouped = transformGroupedPrivileges(groupedPrivileges);

  const { mutateAsync: GetRolePrivilege } = useGetRolePrivileges();

  const getRolePrivileges = useCallback(async () => {
    const body = {
      N_RoleId: selectedRoleId?.toString(),
    };

    const res = await GetRolePrivilege(body);

    const ids = res?.rolePrivileges?.map(
      (item: RolePrivilege) => item.n_PrviligeID
    );

    setSelectedPrivileges(ids);
  }, [GetRolePrivilege, selectedRoleId]);

  useEffect(() => {
    if (selectedRoleId) {
      getRolePrivileges();
    }
  }, [getRolePrivileges, selectedRoleId]);

  const {
    mutateAsync: AddRolePrivileges,
    isPending: isAddRolePrivilegesLoading,
  } = useAddRolePrivileges();

  const handleCheckboxChange = useCallback((id: number) => {
    setSelectedPrivileges((prev) => {
      if (prev && prev.includes(id)) {
        return prev.filter((privilegeId) => privilegeId !== id);
      }
      return prev ? [...prev, id] : [id];
    });
  }, []);

  const onSubmit = useCallback(
    async (data: ManageRolesData) => {
      if (selectedPrivileges.length === 0) {
        toast.error(t("selectPrivileges"));
        return;
      }

      try {
        const rolePrivileges = selectedPrivileges.map((item) => ({
          N_PrviligeID: item,
          N_RoleID: selectedRoleId,
        }));

        const body = {
          Role: {
            N_RoleID: selectedRoleId,
            S_RoleName: data.roleNameEn,
            S_RoleNameAr: data.roleNameAr,
          },
          RolePriviliges: rolePrivileges,
        };

        await AddRolePrivileges(body);

        toast.success("Privilege updated successfully!");

        if (selectedRoleId === USER_ROLES_IDS.ADMIN) {
          const body = {
            N_RoleId: selectedRoleId?.toString(),
          };

          const privileges = await GetRolePrivilege(body);

          setUserRoles(privileges);
        }

        reset({
          roleNameEn: "",
          roleNameAr: "",
        });
        setSelectedPrivileges([]);
        setSelectedRoleId(0);
      } catch (error) {
        toast.error(t("somethingWentWrong"));
      }
    },
    [
      AddRolePrivileges,
      GetRolePrivilege,
      reset,
      selectedPrivileges,
      selectedRoleId,
      setUserRoles,
      t,
    ]
  );

  const handleClear = useCallback(() => {
    reset({
      roleNameEn: "",
      roleNameAr: "",
    });
    setSelectedPrivileges([]);
    setSelectedRoleId(0);
  }, [reset]);

  return (
    <>
      <Toaster position="top-right" reverseOrder={false} />

      <div className="flex flex-col gap-4 md:gap-8">
        <BackButtonWrapper>
          <h1 className="text-xl md:text-2xl dark:text-white">
            {t("manageRoles")}
          </h1>
        </BackButtonWrapper>
        <div className="bg-white p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4"
          >
            <div className="flex flex-col md:flex-row gap-4 items-stretch">
              <div className="md:w-1/2 flex flex-col gap-4 flex-1">
                <Controller
                  name="roleNameEn"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <TextInput
                      label={t("roleNameEnglish")}
                      onChange={onChange}
                      value={value}
                      error={errors?.[name]?.message}
                      required
                    />
                  )}
                />
                <Controller
                  name="roleNameAr"
                  control={control}
                  render={({ field: { onChange, name, value } }) => (
                    <TextInput
                      label={t("roleNameArabic")}
                      onChange={onChange}
                      value={value}
                      required
                      error={errors?.[name]?.message}
                    />
                  )}
                />
                <RolesTable
                  selectedRoleId={selectedRoleId}
                  setSelectedRoleId={setSelectedRoleId}
                  setValue={setValue}
                />
              </div>

              <div className="md:w-1/2 flex flex-col gap-[9px] flex-1 ">
                <label className="text-[13px] font-normal">
                  {t("privilege")}
                </label>

                <div className="flex flex-col gap-2 py-3 px-4 rounded-[15px] border-2">
                  {Object.entries(nestedGrouped).map(([menu, sections]) => (
                    <div
                      key={menu}
                      className="mb-4 border-b pb-2 last:border-0 last:pb-0"
                    >
                      <div className="font-bold mb-1">{t(menu)}</div>

                      {Object.entries(sections).map(([section, privileges]) => (
                        <div key={section} className="ml-4 mb-2">
                          <div className="font-semibold">
                            {t(section.replace(/([A-Z])/g, " $1").trim())}
                          </div>

                          <div className="flex flex-col gap-1 ml-4">
                            {privileges.map((item) => (
                              <CheckboxInput
                                key={item.n_PrviligeID}
                                id={item.n_PrviligeID.toString()}
                                label={`${item.s_PrivNameAr} | ${item.s_PrivName}`}
                                checked={selectedPrivileges?.includes(
                                  item.n_PrviligeID
                                )}
                                setChecked={() =>
                                  handleCheckboxChange(item.n_PrviligeID)
                                }
                                readOnly={
                                  selectedRoleId
                                    ? selectedRoleId === USER_ROLES_IDS.ADMIN ||
                                      selectedRoleId ===
                                        USER_ROLES_IDS.SECURITY ||
                                      selectedRoleId ===
                                        USER_ROLES_IDS.SECURITY_MANAGER ||
                                      selectedRoleId ===
                                        USER_ROLES_IDS.RECEPTION
                                      ? item.s_formName_Web ===
                                        ROUTE_NAMES.HOME_SEARCH
                                      : item.s_formName_Web ===
                                        ROUTE_NAMES.APPROVE_VISIT_REQUEST_SEARCH
                                    : false
                                }
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-2">
              <Button isLoading={isAddRolePrivilegesLoading}>
                {t("save")}
              </Button>
              <Button
                type="button"
                onClick={handleClear}
                className="bg-transparent ring-1 ring-purple-primary text-purple-primary hover:bg-transparent"
              >
                {t("clear")}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ManageRoles;
